import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { saveAs } from "file-saver";
import Sidebar from "../../DesignSystem/Sidebar";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";
import IconButton from "../../DesignSystem/Buttons/IconButton";
import { ReactComponent as ResetIcon } from "../../../assets/icons/reset.svg";
import CustomSelect from "../../DesignSystem/Dropdowns/CustomSelect";
import SearchField from "../../DesignSystem/Inputs/SearchField";
import { ReactComponent as DownloadIcon } from "../../../assets/icons/download.svg";
import { useUserPermissions } from "../../SignIn/UserPermissionsContext";
import { canAccessDepartmentRoute } from "../../Departments/utils/departmentConfig";
import { APP_ROUTES } from "../../../constants/appRoutes";
import { subscribeAdvisersData } from "../../../context/Adviser/Utils/ExportAdvisersData";
import { exportAdviserAppointmentsExcel } from "../../Adviser/Utils/exportAdviserAppointmentsExcel";
import { exportAdviserDobAgeExcel } from "../../Adviser/Utils/exportAdviserDobAgeExcel";
import { exportAdviserStatusExcel } from "../../Adviser/Utils/exportAdviserStatusExcel";
import { exportAdviserAddressesExcel } from "../../Adviser/Utils/exportAdviserAddressesExcel";

const REPORTS = [
  {
    id: "addresses",
    name: "Adviser Addresses",
    description: "All advisers with their name, status, current address and business address.",
    export: exportAdviserAddressesExcel,
  },
  {
    id: "status",
    name: "Adviser Status",
    description:
      "All advisers with their status, appointment status and email.",
    export: exportAdviserStatusExcel,
  },
  {
    id: "appointments",
    name: "Adviser Appointment Dates",
    description:
      "All advisers with their appointment date, email and appointment status.",
    export: exportAdviserAppointmentsExcel,
  },
  {
    id: "dob",
    name: "Adviser Date of Birth and Age",
    description:
      "All advisers with their appointment status, date of birth and current age.",
    export: exportAdviserDobAgeExcel,
  },
];

export default function ComplianceReportsPage() {
  const { userPermissions = [] } = useUserPermissions();
  const canAccess = canAccessDepartmentRoute(userPermissions, APP_ROUTES.COMPLIANCE_REPORTS);
  const [advisers, setAdvisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [running, setRunning] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [filters, setFilters] = useState({ search: "", category: "" });

  useEffect(() => {
    if (!canAccess) return undefined;
    return subscribeAdvisersData(
      (data) => {
        setAdvisers(data);
        setLoading(false);
      },
      () => {
        setError("Could not load adviser data. Please refresh and try again.");
        setAdvisers([]);
        setLoading(false);
      },
      { waitForPersonalDetails: true },
    );
  }, [canAccess]);

  const runReport = async (report) => {
    setRunning(report.id);
    setError("");
    try {
      const { blob, filename } = await report.export(advisers);
      saveAs(blob, filename);
    } catch {
      setError(`Could not create ${report.name}. Please try again.`);
    } finally {
      setRunning(null);
    }
  };

  if (!canAccess) return <Navigate to="/Department/AllStaff" replace />;
  const visibleReports = REPORTS.filter(
    (report) =>
      `${report.name} ${report.description}`
        .toLowerCase()
        .includes(filters.search.trim().toLowerCase()) &&
      (!filters.category || filters.category === "Compliance"),
  );

  return (
    <div className="main-content">
      <Helmet>
        <title>Adviser Reports</title>
      </Helmet>
      <Sidebar />
      <div className="Main-Container table-contained-page fit-column gap-xl">
        <Breadcrumb
          items={[
            { label: "Compliance", to: "/Department/Compliance" },
            { label: "Adviser Reports" },
          ]}
        />
        <div className="row-wrap justify-space-between align-center gap-m">
          <div>
            <div className="FP_H3">Adviser Reports</div>
            {/* <p>Download adviser reports as Excel workbooks.</p> */}
          </div>
        </div>

        {error && (
          <p className="FP_Content text-error" role="alert">
            {error}
          </p>
        )}
        <header className="inline-flex flex-wrap align-end gap-m">
          <div className="width-fit-content">
            <SearchField
              id="compliance-report-search"
              label="Search reports"
              placeholder="Report name or description"
              value={search}
              onChange={(event) => {
                const value = event.target.value;
                setSearch(value);
                setFilters((previous) => ({ ...previous, search: value }));
              }}
              OnSearch={() => setFilters({ search, category })}
            />
          </div>
          <div className="width-fit-content">
            <CustomSelect
              label="Category"
              options={[{ id: "Compliance", name: "Compliance" }]}
              value={category}
              onChange={(event) => {
                const value = event.target.value;
                setCategory(value);
                setFilters((previous) => ({ ...previous, category: value }));
              }}
              placeholder="All categories"
              searchable={false}
            />
          </div>
          <div className="inline-flex align-center gap-s">
            <IconButton
              icon={<ResetIcon aria-hidden="true" />}
              label="Reset report filters"
              onClick={() => {
                setSearch("");
                setCategory("");
                setFilters({ search: "", category: "" });
              }}
            />
          </div>
        </header>
        <section className="table-scroll-card" aria-label="Adviser reports">
          <div className="table-scroll-container">
            <table className="website-table">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="FP_Help_Text_Semibold table-index-column"
                  >
                    
                  </th>
                  <th scope="col" className="FP_Help_Text_Semibold">
                    Report Name
                  </th>
                  <th scope="col" className="FP_Help_Text_Semibold">
                    Description
                  </th>
                  <th scope="col" className="FP_Help_Text_Semibold">
                    Type
                  </th>
                  <th scope="col" className="FP_Help_Text_Semibold">
                    Category
                  </th>
                  <th scope="col" className="FP_Help_Text_Semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleReports.map((report, index) => (
                  <tr key={report.id}>
                    <td className="FP_Help_Text black table-index-column">
                      {index + 1}.
                    </td>
                    <td className="FP_Help_Text black">{report.name}</td>
                    <td className="FP_Help_Text black">{report.description}</td>
                    <td className="FP_Help_Text black">
                      <span className="FP_Help_Text">XLSX</span>
                    </td>
                    <td className="FP_Help_Text black">Compliance</td>
                    <td className="FP_Help_Text black table-action-column">
                      <IconButton
                        icon={<DownloadIcon aria-hidden="true" />}
                        label={`Download ${report.name} Excel`}
                        loading={running === report.id}
                        loadingLabel={`Preparing ${report.name} Excel…`}
                        disabled={loading || !!running || advisers.length === 0}
                        onClick={() => runReport(report)}
                      />
                    </td>
                  </tr>
                ))}
                {!visibleReports.length && (
                  <tr>
                    <td
                      colSpan="6"
                      className="FP_Help_Text black text-align-center"
                    >
                      No reports match your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
